import React from 'react';
import { connect } from 'react-redux';
import { Card, CardContent, CardMedia, Grid, CardActions, IconButton } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { mediaPickerActions } from '../../../store';
import AspectRatioRoundedIcon from '@mui/icons-material/AspectRatioRounded';
import { createMediaSource, createMediaSourceDataForVideo, createLocalMediaSourceUrls, getVideoUrlForResolution } from '../../../../helpers';
import { MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, VIDEO_RESOLUTIONS } from '../../../../CreatePageConstants';
import { createActions } from '../../../../store';
import { AnalyticsConstants, trackEvent } from '../../../../../../../utilities/analyticsUtilities';
import AppConstants from '../../../../../../../App/AppConstants';
import { getVideoElementPlaying } from '../../../../../../../utilities/basicUtilities';
import { isSafariBrowser } from '../../../../../../../utilities/browserUtilities';
import ProMediaCardLabel from '../../../common/ProMediaCardLabel';

const styles = theme => ({
    videoCard: {
        margin: theme.spacing(0.5),
        '&:hover': {
            '& .MuiCardActions-root': {
                display: 'flex'
            }
        },
        overflow: 'hidden',
        cursor: 'pointer'
    },
    videoCardContent: {
        height: "100%",
        padding: 0
    },
    videoCardActions: {
        justifyContent: 'flex-end',
        marginTop: -125,
        display: 'none'
    },
    previewButton: {
        padding: 2
    }
});

const BackgroundCatalogVideosCard = (props) => {
    const { 
        classes, catalogVideo, galleryCardWidth, galleryCardHeight, showCatalogVideoPreview, setMediaPickersProperty, 
        updateBackgroundSettings, videoSettingsId, setPageProperty, subscriptionType, openPremiumVideosDialog,
        savingInProgress, showCriticalOperationProgressWarning, isVideoExportsQueued
    } = props;

    const isSafari = isSafariBrowser();
    const professionalCatalogVideo = (catalogVideo.isPremium && subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE);
    const p360Video = getVideoUrlForResolution(catalogVideo.catalogVideoUrls, VIDEO_RESOLUTIONS.p360.height);
    const p540Video = getVideoUrlForResolution(catalogVideo.catalogVideoUrls, VIDEO_RESOLUTIONS.p540.height);
    const p720Video = getVideoUrlForResolution(catalogVideo.catalogVideoUrls, VIDEO_RESOLUTIONS.p720.height);
    const p1080Video = getVideoUrlForResolution(catalogVideo.catalogVideoUrls, VIDEO_RESOLUTIONS.p1080.height);
    
    const onVideoClick = () => {
        if (savingInProgress) {
            showCriticalOperationProgressWarning();
            return;
        }

        if (isVideoExportsQueued())
            return;
        
        if (professionalCatalogVideo) {
            setMediaPickersProperty('backgroundMediaPickerOpen', false);
            openPremiumVideosDialog(catalogVideo.exampleUrl, () => {
                setMediaPickersProperty('backgroundMediaPickerOpen', true);
            }, 'catalog videos');
        } 
        else {
            const mediaData = createMediaSourceDataForVideo(p1080Video.url, p720Video.url, p540Video.url, p360Video.url);
            const mediaSource = createMediaSource(mediaData, MEDIA_SOURCE_TYPE.VIMEO, catalogVideo.id, MEDIA_RESOURCE_TYPE.VIDEO);
    
            setPageProperty('backgroundVideosLoading', true);
            createLocalMediaSourceUrls(mediaSource,
            (localMediaSource) => {
                updateBackgroundSettings({ mediaSource: localMediaSource });
                setPageProperty('backgroundVideosLoading', false);
            }, () => {
                setPageProperty('backgroundVideosLoading', false);
            }, window.createPageAbortController);
    
            setMediaPickersProperty('backgroundMediaPickerOpen', false);

            trackEvent('Assigned Media', {
                category: AnalyticsConstants.VIDEO_CATEGORY,
                type: 'background',
                source: 'Catalog Videos',
                videoId: videoSettingsId,
                catalogVideoId: catalogVideo.id
            });
        }
    };

    const onPreviewClick = (event) => {
        showCatalogVideoPreview(catalogVideo.id, catalogVideo.isPremium, {
            exampleUrl: catalogVideo.exampleUrl, 
            p1080Url: p1080Video.url, 
            p720Url: p720Video.url, 
            p540Url: p540Video.url, 
            p360Url: p360Video.url
        });
        event.stopPropagation();
    };

    const onCardMouseEnter = (event) => {
        const videoElement = event.currentTarget.querySelector('video');
        let isPlaying = getVideoElementPlaying(videoElement);

        if (!isPlaying) {
            if (videoElement) {
                videoElement.play().catch(error => {
                    console.error('Failed to play video:', error);
                });
            }
        }
    };

    const onCardMouseLeave = (event) => {
        const videoElement = event.currentTarget.querySelector('video');
        let isPlaying = getVideoElementPlaying(videoElement);

        if (isPlaying)
            videoElement.pause();
    };

    return (
        <Grid>
            <Card 
                className={classes.videoCard}
                style={{width: galleryCardWidth, height: galleryCardHeight}}
                onClick={onVideoClick}
                onMouseEnter={isSafari ? null : onCardMouseEnter}
                onMouseLeave={isSafari ? null : onCardMouseLeave}
            >
                <CardContent className={classes.videoCardContent}>
                    {professionalCatalogVideo && <ProMediaCardLabel align="start" width={208} />}

                    {isSafari 
                    ?
                        <CardMedia
                            component={'img'}
                            src={catalogVideo.thumbnail}
                        />
                    :
                        <CardMedia
                            component={'video'}
                            poster={catalogVideo.thumbnail}
                            src={p360Video.url}
                            muted
                            loop
                            type='video/mp4'
                            preload='auto'
                        />
                    }
                </CardContent>
                <CardActions className={classes.videoCardActions}>
                    <IconButton
                        className={classes.previewButton}
                        aria-label='open video preview'
                        onClick={onPreviewClick}
                        size="large"
                    >
                        <AspectRatioRoundedIcon />
                    </IconButton>
                </CardActions>
            </Card>
        </Grid>
    );
}

const mapStateToProps = (state, ownProps) => {
    const catalogVideo = state.mediaPicker.backgroundCatalogVideos[ownProps.index];
    const { id: videoSettingsId } = state.createPreview.present.videoSettings;
    const { subscriptionType } = state.app.accountDetails;
    const { savingInProgress } = state.create.videoSavingState;

    return { 
        catalogVideo, 
        videoSettingsId,
        subscriptionType,
        savingInProgress
    };
}

const mapDispatchToProps = (dispatch) => {
    const isVideoExportsQueued = () => dispatch(createActions.isVideoExportsQueued());
    const setMediaPickersProperty = (key, value) => dispatch(mediaPickerActions.setProperty(key, value));
    const setPageProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const showCatalogVideoPreview = (catalogVideoId, isPremium, urls) => dispatch(mediaPickerActions.showCatalogVideoPreview(catalogVideoId, isPremium, urls));
    const updateBackgroundSettings = (properties) => dispatch(createActions.updateBackgroundSettings(properties));
    const openPremiumVideosDialog = (videoUrl, onGoBack, location) => dispatch(mediaPickerActions.openPremiumVideosDialog(videoUrl, onGoBack, location));
    const showCriticalOperationProgressWarning = () => dispatch(createActions.showCriticalOperationProgressWarning());

    return { 
        setMediaPickersProperty,
        setPageProperty,
        showCatalogVideoPreview,
        updateBackgroundSettings,
        openPremiumVideosDialog,
        isVideoExportsQueued,
        showCriticalOperationProgressWarning
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(BackgroundCatalogVideosCard, styles));